// src/components/TripItinerary.tsx
import React, {forwardRef, JSX} from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCar, FaMapMarkerAlt, FaUtensils, FaFlag, FaHome, FaCamera } from 'react-icons/fa';
import { IconBaseProps } from 'react-icons/lib';

// Wrappers for icons
// @ts-ignore
const Car = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaCar {...props} ref={ref} /> as JSX.Element);
Car.displayName = 'Car';
// @ts-ignore
const MapMarkerAlt = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaMapMarkerAlt {...props} ref={ref} /> as JSX.Element);
MapMarkerAlt.displayName = 'MapMarkerAlt';
// @ts-ignore
const Utensils = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaUtensils {...props} ref={ref} /> as JSX.Element);
Utensils.displayName = 'Utensils';
// @ts-ignore
const Flag = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaFlag {...props} ref={ref} /> as JSX.Element);
Flag.displayName = 'Flag';
// @ts-ignore
const Home = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaHome {...props} ref={ref} /> as JSX.Element);
Home.displayName = 'Home';
// @ts-ignore
const Camera = forwardRef<SVGSVGElement, IconBaseProps>((props, ref) => <FaCamera {...props} ref={ref} /> as JSX.Element);
Camera.displayName = 'Camera';

// Styled Components
const ItinerarySection = styled.section`
    padding: 3rem 0;
    background: #ffffff;
`;

const ItineraryContainer = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
`;

const SectionTitle = styled.h2`
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 2rem;
    position: relative;

    &:after {
        content: '';
        position: absolute;
        bottom: -0.5rem;
        left: 0;
        width: 60px;
        height: 3px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        border-radius: 2px;
    }
`;

const ContentWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: start;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 2rem;
    }
`;

const TimelineContainer = styled.div`
    position: relative;
    padding-left: 2rem;

    &:before {
        content: '';
        position: absolute;
        left: 1rem;
        top: 1.5rem;
        bottom: 1.5rem;
        width: 2px;
        background: linear-gradient(to bottom, #667eea, #764ba2);
        border-radius: 1px;
    }
`;

const TimelineItem = styled(motion.div)<{ isOptional?: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
    position: relative;
    padding: 1.25rem;
    background: ${props => props.isOptional ? '#fefce8' : '#f8fafc'};
    border-radius: 12px;
    border: 1px solid ${props => props.isOptional ? '#fef3c7' : '#e5e7eb'};
    margin-left: ${props => props.isOptional ? '2rem' : '0'};
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        border-color: #667eea;
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const TimelineMarker = styled.div<{ type: string }>`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${props => {
    switch (props.type) {
        case 'pickup':
        case 'dropoff':
            return '#667eea';
        case 'stop':
            return '#10b981';
        case 'lunch':
            return '#f59e0b';
        case 'van':
            return '#6b7280';
        case 'optional':
            return '#d97706';
        default:
            return '#667eea';
    }
}};
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: absolute;
    left: -1.5rem;
    top: 1.5rem;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        color: white;
        font-size: 0.7rem;
    }
`;

const ItemContent = styled.div`
    flex: 1;
`;

const ItemHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;
`;

const ItemTitle = styled.h3`
    font-size: 1.1rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
`;

const ItemTime = styled.span`
    font-size: 0.85rem;
    font-weight: 600;
    color: #667eea;
    background: rgba(102, 126, 234, 0.1);
    padding: 0.2rem 0.6rem;
    border-radius: 10px;
`;

const ItemDescription = styled.p`
    color: #666;
    line-height: 1.4;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
`;

const LocationHighlight = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.25rem;
    color: #667eea;
    font-weight: 500;
    font-size: 0.85rem;
`;

const OptionalBadge = styled.span`
    font-size: 0.8rem;
    font-weight: 600;
    color: #d97706;
    background: #fef3c7;
    padding: 0.2rem 0.6rem;
    border-radius: 10px;
    border: 1px solid #fbbf24;
`;

const ExtraFeeBadge = styled.span`
    font-size: 0.8rem;
    font-weight: 600;
    color: #dc2626;
    background: #fee2e2;
    padding: 0.2rem 0.6rem;
    border-radius: 10px;
    border: 1px solid #fca5a5;
    margin-left: 0.5rem;
`;

const ActivityList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.5rem;
`;

const ActivityTag = styled.span`
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.2rem 0.5rem;
    border-radius: 8px;
    background: #f3f4f6;
    color: #374151;
`;

const MapContainer = styled.div`
    height: 500px;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
    position: sticky;
    top: 2rem;

    @media (max-width: 968px) {
        height: 350px;
    }
`;

const MapFrame = styled.iframe`
    width: 100%;
    height: 100%;
    border: 0;
`;

const MapFallback = styled.div`
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 1.1rem;
    text-align: center;
    padding: 2rem;
    
    p {
        margin: 0;
        line-height: 1.6;
    }
`;

// Component
const TripItinerary: React.FC = () => {
    const [mapError, setMapError] = React.useState(false);

    const itineraryItems = [
        {
            type: 'pickup',
            title: 'Pickup',
            description: 'Pickup from your hotel or accommodation in Bratislava',
            time: '',
            locations: ['Bratislava'],
            icon: Home,
        },
        {
            type: 'van',
            title: 'Van Journey',
            description: 'Travel to first destination',
            time: '40 minutes',
            icon: Car,
        },
        {
            type: 'stop',
            title: 'Červený Kameň Castle',
            description: 'Historic castle visit with guided tour',
            time: '1 hour',
            activities: ['Break time', 'Photo stop', 'Visit', 'Guided tour', 'Free time'],
            icon: MapMarkerAlt,
        },
        {
            type: 'van',
            title: 'Van Journey',
            description: 'Travel to next destination',
            time: '30 minutes',
            icon: Car,
        },
        {
            type: 'lunch',
            title: 'Smolenice - Lunch',
            description: 'Lunch break in Smolenice',
            time: '45 minutes',
            icon: Utensils,
        },
        {
            type: 'stop',
            title: 'Smolenice Castle',
            description: 'Romantic neo-Gothic château with a tower and park',
            time: '1 hour',
            activities: ['Break time', 'Photo stop', 'Guided tour', 'Free time'],
            icon: MapMarkerAlt,
        },
        {
            type: 'van',
            title: 'Van Journey',
            description: 'Travel to Trenčín',
            time: '75 minutes',
            icon: Car,
        },
        {
            type: 'stop',
            title: 'Trenčín',
            description: 'Historic city center exploration',
            time: '75 minutes',
            activities: ['Break time', 'Photo stop', 'Visit', 'Guided tour', 'Free time', 'Shopping', 'Walk'],
            icon: MapMarkerAlt,
        },
        {
            type: 'van',
            title: 'Van Journey',
            description: 'Travel to traditional village',
            time: '55 minutes',
            icon: Car,
        },
        {
            type: 'stop',
            title: 'Čičmany',
            description: 'Traditional folk architecture village',
            time: '1 hour',
            activities: ['Break time', 'Photo stop', 'Visit', 'Guided tour'],
            icon: MapMarkerAlt,
        },
        {
            type: 'van',
            title: 'Van Journey',
            description: 'Short transfer',
            time: '15 minutes',
            icon: Car,
        },
        {
            type: 'optional',
            title: 'Slovak Bethlehem',
            description: 'Traditional nativity scene exhibition',
            time: '30 minutes',
            activities: ['Photo stop', 'Visit', 'Guided tour'],
            optional: true,
            icon: Camera,
        },
        {
            type: 'van',
            title: 'Van Journey',
            description: 'Travel to final destination',
            time: '110 minutes',
            icon: Car,
        },
        {
            type: 'stop',
            title: 'Bratislava Castle',
            description: 'Discover rich history behind the castle',
            time: '50 minutes',
            activities: ['Break time', 'Photo stop'],
            icon: MapMarkerAlt,
        },
        {
            type: 'van',
            title: 'Van Journey',
            description: 'Return journey',
            time: '45 minutes',
            icon: Car,
        },
        {
            type: 'dropoff',
            title: 'Drop-off',
            description: 'Return to your original pickup point',
            time: '',
            locations: ['Bratislava'],
            icon: Flag,
        },
    ];

    // Simple map showing Bratislava area
    const simpleMapUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d85118.55224629566!2d17.10701127639101!3d48.14212719466141!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476c8937d60d4f4f%3A0x86ec4a2c6dc3d6f1!2sBratislava%2C%20Slovakia!5e0!3m2!1sen!2sus!4v1690000000000";

    return (
        <ItinerarySection>
            <ItineraryContainer>
                <SectionTitle>Itinerary</SectionTitle>

                <ContentWrapper>
                    <TimelineContainer>
                        {itineraryItems.map((item, index) => (
                            <TimelineItem
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                viewport={{ once: true }}
                                isOptional={item.optional}
                            >
                                <TimelineMarker type={item.type}>
                                    <item.icon />
                                </TimelineMarker>

                                <ItemContent>
                                    <ItemHeader>
                                        <ItemTitle>{item.title}</ItemTitle>
                                        {item.time && <ItemTime>{item.time}</ItemTime>}
                                        {item.optional && <OptionalBadge>Optional</OptionalBadge>}
                                    </ItemHeader>

                                    {item.description && (
                                        <ItemDescription>{item.description}</ItemDescription>
                                    )}

                                    {item.locations && (
                                        <LocationHighlight>
                                            <MapMarkerAlt />
                                            {item.locations.join(', ')}
                                        </LocationHighlight>
                                    )}

                                    {item.activities && (
                                        <ActivityList>
                                            {item.activities.map((activity, activityIndex) => (
                                                <ActivityTag key={activityIndex}>
                                                    {activity}
                                                </ActivityTag>
                                            ))}
                                        </ActivityList>
                                    )}
                                </ItemContent>
                            </TimelineItem>
                        ))}
                    </TimelineContainer>

                    <MapContainer>
                        {mapError ? (
                            <MapFallback>
                                <div>
                                    <p><strong>Tour Route Map</strong></p>
                                    <p>Bratislava → Červený Kameň Castle → Smolenice → Trenčín → Čičmany → Bratislava Castle</p>
                                    <p style={{ fontSize: '0.9rem', marginTop: '1rem', color: '#999' }}>
                                        Interactive map unavailable. This area shows the tour route through Slovakia's castles and traditional villages.
                                    </p>
                                </div>
                            </MapFallback>
                        ) : (
                            <MapFrame
                                src={simpleMapUrl}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Slovakia Castles Tour Route"
                                onError={() => setMapError(true)}
                            />
                        )}
                    </MapContainer>
                </ContentWrapper>
            </ItineraryContainer>
        </ItinerarySection>
    );
};

export default TripItinerary;